'use client'

import { Accordion, SkeletonCircle, SkeletonText } from '@sushiswap/ui'
import React from 'react'
import { KATANA_CHAIN_ID } from 'src/config'
import { useConcentratedLiquidityPositions } from 'src/lib/wagmi/hooks/positions/hooks/useConcentratedLiquidityPositions'
import { formatUSD } from 'sushi'
import { isSushiSwapV3ChainId } from 'sushi/evm'
import { useConnection } from 'wagmi'
import { PortfolioV3Positions } from './portfolio-v3-positions'

const V3_CHAIN_IDS = isSushiSwapV3ChainId(KATANA_CHAIN_ID)
  ? ([KATANA_CHAIN_ID] as const)
  : ([] as const)

export const PortfolioPositions = () => {
  const { address } = useConnection()

  const {
    data: positions,
    isInitialLoading,
    isError,
  } = useConcentratedLiquidityPositions({
    account: address,
    chainIds: V3_CHAIN_IDS,
  })

  const activePositions = positions?.filter((p) => p.liquidity !== 0n)
  const totalUSD = activePositions?.reduce(
    (sum, p) => sum + p.position.positionUSD,
    0,
  )

  return (
    <div className="flex flex-col h-full overflow-hidden gap-y-5">
      <div className="px-5">
        <div className="flex flex-col px-5 py-3 gap-y-3 bg-secondary rounded-xl">
          <span className="text-sm text-muted-foreground">Total Balance</span>
          <div className="">
            {isInitialLoading && !positions && !isError ? (
              <SkeletonText fontSize="lg" className="!w-1/3" />
            ) : (
              <>
                <div className="text-2xl font-bold">
                  {formatUSD(totalUSD ?? 0)}
                </div>
                {isError ? (
                  <div className="text-xs italic text-red-500">
                    An error occurred fetching positions.
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
      {isError ? null : isInitialLoading ? (
        <div>
          <div className="px-5 py-4">
            <SkeletonText />
          </div>
          {Array.from({ length: 12 }).map((_, i) => (
            <div
              key={`${i}`}
              className="flex items-center w-full px-5 py-3 gap-x-7"
            >
              <div className="flex items-center">
                <SkeletonCircle radius={28} />
                <SkeletonCircle radius={28} className="-ml-[12px]" />
              </div>
              <div className="flex items-center justify-between w-full gap-x-3">
                <div className="flex flex-col basis-3/4 gap-y-1">
                  <SkeletonText fontSize="sm" />
                  <SkeletonText fontSize="xs" />
                </div>
                <div className="flex flex-col basis-1/4 gap-y-1">
                  <SkeletonText fontSize="sm" />
                  <SkeletonText fontSize="xs" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Accordion
          type="multiple"
          className="h-full overflow-y-auto"
          defaultValue={['v3']}
        >
          {activePositions?.length ? (
            <PortfolioV3Positions positions={activePositions} />
          ) : null}
        </Accordion>
      )}
    </div>
  )
}
